import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Trash2 } from "lucide-react";
import { roomsApi, roomQueryKeys, type Room } from "../../shared/api/rooms";
import { apiClient, apiError } from "../../shared/api/client";
import { DepartmentSelect } from "../../shared/ui/DepartmentSelect";
import { useRowSelection, BulkDeleteBar, runBulkDelete } from "../../shared/ui/bulkSelect";
import { useToast } from "../../shared/ui/Toast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { HIGHLIGHT_ROW_CLASS, useHighlightRow } from "../../shared/hooks/useHighlightRow";

interface DoctorOpt {
  id: string;
  fullName?: string | null;
  specialty?: string | null;
}

/** Phòng khám — quản lý các phòng khám của chi nhánh + bác sĩ mặc định. */
export function RoomsSettingsPage() {
  const highlight = useHighlightRow(); // #19683 — Audit log trỏ tới một dòng
  const qc = useQueryClient();
  const rooms = useQuery({
    queryKey: roomQueryKeys.list(false),
    queryFn: () => roomsApi.list(false),
  });
  const doctors = useQuery({
    queryKey: ["doctors-options-rooms"],
    queryFn: () =>
      apiClient
        .get<{ content: DoctorOpt[] }>("/v1/doctors", { params: { size: 100 } })
        .then((r) => r.data.content),
  });

  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [doctorId, setDoctorId] = useState("");
  const [departmentId, setDepartmentId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();
  const sel = useRowSelection();
  const [bulkPending, setBulkPending] = useState(false);

  const create = useMutation({
    mutationFn: () =>
      roomsApi.create({
        code,
        name,
        doctorProfileId: doctorId || null,
        departmentId,
      }),
    onSuccess: () => {
      setCode("");
      setName("");
      setDoctorId("");
      setDepartmentId(null);
      setError(null);
      qc.invalidateQueries({ queryKey: roomQueryKeys.list(false) });
    },
    onError: (e) => setError(apiError(e).detail),
  });
  const archive = useMutation({
    mutationFn: (id: string) => roomsApi.archive(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: roomQueryKeys.list(false) }),
  });
  // Xoá vĩnh viễn — chỉ được khi phòng chưa gắn lịch hẹn (backend trả 409 room_in_use nếu vướng).
  const remove = useMutation({
    mutationFn: (id: string) => roomsApi.remove(id),
    onSuccess: () => {
      setError(null);
      qc.invalidateQueries({ queryKey: roomQueryKeys.list(false) });
    },
    // Row-action error → toast (always visible near the top), not a message at the far bottom of the
    // page the user never scrolls to — which made them click delete 4× thinking nothing happened
    // (#18586).
    onError: (e) => toast.error(apiError(e).detail),
  });
  // Mở lại phòng đã ngừng — PUT /v1/rooms/{id} với active = true (giữ nguyên mã/tên/bác sĩ).
  const reactivate = useMutation({
    mutationFn: (r: Room) =>
      roomsApi.update(r.id, {
        code: r.code,
        name: r.name,
        doctorProfileId: r.doctorProfileId,
        departmentId: r.departmentId,
        active: true,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: roomQueryKeys.list(false) }),
    onError: (e) => toast.error(apiError(e).detail),
  });

  const list = rooms.data ?? [];

  async function bulkDelete() {
    setBulkPending(true);
    const { failed } = await runBulkDelete([...sel.selectedIds], roomsApi.remove);
    setBulkPending(false);
    sel.clear();
    qc.invalidateQueries({ queryKey: roomQueryKeys.list(false) });
    if (failed.length) {
      // Name the rooms that failed (they are in use) + point to "Ngừng" — a bare count left the
      // user guessing which of the selected rooms could not be deleted (#18586).
      const names = failed
        .map((id) => list.find((r) => r.id === id)?.name ?? id)
        .join(", ");
      toast.error(
        `Không xoá được ${failed.length} phòng (đang có lịch hẹn): ${names}. Hãy dùng "Ngừng" để ẩn.`,
      );
    }
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">Phòng khám</h1>
        <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
          Khai báo các phòng khám để cấp số thứ tự riêng theo từng phòng và hiển thị
          lên màn hình phòng chờ. Mỗi phòng có một <strong>màn hình hàng đợi riêng</strong>:
          bấm "Mở màn hình hàng đợi" (hoặc sao chép link) rồi mở trên TV/màn hình đặt tại
          phòng đó — nó chỉ hiện số thứ tự của phòng này. Phòng khám 1 phòng có thể bỏ qua mục này.
        </p>
      </div>

      <div className="grid max-w-4xl gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Danh sách phòng</CardTitle>
          </CardHeader>
          <CardContent>
            {rooms.isLoading ? (
              <p className="text-sm text-muted-foreground">Đang tải…</p>
            ) : list.length === 0 ? (
              <p className="text-sm text-muted-foreground">Chưa có phòng nào.</p>
            ) : (
              <>
                <BulkDeleteBar
                  count={sel.count}
                  noun="phòng"
                  pending={bulkPending}
                  onClear={sel.clear}
                  onDelete={bulkDelete}
                />
                <label className="mb-2 flex items-center gap-2 px-1 text-xs text-muted-foreground">
                  <input
                    type="checkbox"
                    aria-label="Chọn tất cả"
                    className="h-4 w-4 rounded border-input accent-primary"
                    checked={list.length > 0 && list.every((r) => sel.selectedIds.has(r.id))}
                    ref={(el) => {
                      if (el)
                        el.indeterminate =
                          list.some((r) => sel.selectedIds.has(r.id)) &&
                          !list.every((r) => sel.selectedIds.has(r.id));
                    }}
                    onChange={() => {
                      const next = new Set(sel.selectedIds);
                      if (list.every((r) => next.has(r.id)))
                        list.forEach((r) => next.delete(r.id));
                      else list.forEach((r) => next.add(r.id));
                      sel.setSelectedIds(next);
                    }}
                  />
                  Chọn tất cả
                </label>
                <div className="grid gap-3 sm:grid-cols-2">
                  {list.map((r) => (
                    <div
                      key={r.id}
                      ref={highlight.isHighlighted(r.id) ? highlight.scrollRef : undefined}
                      className={`rounded-lg border bg-card p-3 ${
                        sel.selectedIds.has(r.id) ? "border-primary/50 ring-1 ring-primary/30" : ""
                      } ${highlight.isHighlighted(r.id) ? HIGHLIGHT_ROW_CLASS : ""}`}
                    >
                      <div className="flex items-start gap-2.5">
                        <input
                          type="checkbox"
                          aria-label="Chọn phòng"
                          className="mt-1 h-4 w-4 shrink-0 rounded border-input accent-primary"
                          checked={sel.selectedIds.has(r.id)}
                          onChange={() => sel.toggle(r.id)}
                        />
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-semibold text-foreground">{r.code}</span>
                            {r.active ? (
                              <Badge variant="success">Đang dùng</Badge>
                            ) : (
                              <Badge variant="muted">Ngừng</Badge>
                            )}
                          </div>
                          <div className="truncate font-medium text-foreground">{r.name}</div>
                          <div className="truncate text-sm text-muted-foreground">
                            Bác sĩ: {r.doctorName ?? "—"}
                          </div>
                        </div>
                      </div>

                      {/* Per-room TV link moved to Cài đặt → Vận hành → "Màn hình & thiết bị"
                          (/settings/displays) so device setup lives in one place (#18583/#18589). */}
                      <div className="mt-3 flex flex-wrap items-center gap-1.5 border-t pt-2.5">
                        {r.active ? (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            disabled={archive.isPending}
                            onClick={() => archive.mutate(r.id)}
                          >
                            Ngừng
                          </Button>
                        ) : (
                          <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            disabled={reactivate.isPending}
                            onClick={() => reactivate.mutate(r)}
                          >
                            Mở lại
                          </Button>
                        )}
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="ml-auto text-destructive hover:text-destructive"
                          disabled={remove.isPending}
                          title="Xoá vĩnh viễn phòng (chỉ khi chưa có lịch hẹn)"
                          onClick={() => {
                            if (confirm(`Xoá vĩnh viễn phòng "${r.name}"? Không thể hoàn tác.`))
                              remove.mutate(r.id);
                          }}
                        >
                          <Trash2 />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Thêm phòng</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-3">
            <div className="flex flex-wrap items-end gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="room-code">Mã phòng</Label>
                <Input
                  id="room-code"
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="VD: P1"
                  className="w-28"
                />
              </div>
              <div className="min-w-[180px] flex-1 space-y-1.5">
                <Label htmlFor="room-name">Tên phòng</Label>
                <Input
                  id="room-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="VD: Phòng khám Nội 1"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="room-doctor">Bác sĩ mặc định</Label>
                <select
                  id="room-doctor"
                  value={doctorId}
                  onChange={(e) => setDoctorId(e.target.value)}
                  className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <option value="">— Không chọn —</option>
                  {(doctors.data ?? []).map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.fullName ?? "Bác sĩ"}
                      {d.specialty ? ` · ${d.specialty}` : ""}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="room-department">Khoa</Label>
                <DepartmentSelect id="room-department" value={departmentId} onChange={setDepartmentId} />
              </div>
              <Button
                type="button"
                disabled={!code.trim() || !name.trim() || create.isPending}
                onClick={() => create.mutate()}
              >
                Thêm phòng
              </Button>
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
